/**
 * cart-drawer.js — v21
 *
 * Fixes applied vs v20:
 * 1. Shipping bar threshold now reads from window.themeSettings.shippingThreshold
 *    (set in theme.liquid as settings.cart_free_shipping_threshold * 100)
 * 2. Subscribe upsell uses /cart/change.js (remove + re-add with selling_plan)
 *    fetched from /products/{handle}.js — no more redirect to PDP
 * 3. Cart footer subtotal updated by JS after every cart change
 * 4. renderCart() properly updates all DOM regions
 * 5. open() waits for refresh() before showing drawer
 * 6. Cart count badge updates correctly
 * 7. Subscribe upsell button shows for ALL non-subscription items
 *    (not gated on item.product.selling_plan_groups which /cart.js doesn't return)
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.closeBtn = this.drawer?.querySelector('.cart-drawer__close');
    this.itemsContainer = this.drawer?.querySelector('.cart-drawer__items');
    this.shippingFill = this.drawer?.querySelector('.cart-drawer__shipping-fill');
    this.shippingUnlock = this.drawer?.querySelector('.cart-drawer__shipping-unlock');
    this.cartCountEls = document.querySelectorAll('.cart-count');

    if (!this.drawer) return;

    // Threshold in cents — set in theme.liquid as settings.cart_free_shipping_threshold * 100
    this.threshold = window.themeSettings?.shippingThreshold || 7000;

    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-open-cart]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    document.addEventListener('cart:open', () => this.open());

    this.drawer?.addEventListener('click', (e) => {
      const qtyBtn = e.target.closest('.cart-item__qty-btn');
      if (qtyBtn) {
        this.updateQuantity(qtyBtn.dataset.key, qtyBtn.dataset.delta);
        return;
      }
      const removeBtn = e.target.closest('.cart-item__remove');
      if (removeBtn) {
        this.removeItem(removeBtn.dataset.key);
        return;
      }
      const subscribeBtn = e.target.closest('.cart-item__subscribe-btn');
      if (subscribeBtn) {
        this.upgradeToSubscription(subscribeBtn.dataset.key, subscribeBtn.dataset.handle);
        return;
      }
    });
  }

  isOpen() {
    return this.drawer?.classList.contains('open');
  }

  open() {
    this.refresh().then(() => {
      this.drawer?.classList.add('open');
      this.overlay?.classList.add('open');
      document.body.style.overflow = 'hidden';
      this.drawer?.focus();
    });
  }

  close() {
    this.drawer?.classList.remove('open');
    this.overlay?.classList.remove('open');
    document.body.style.overflow = '';
  }

  async safeJson(response) {
    const text = await response.text();
    try { return JSON.parse(text); }
    catch (e) { throw new Error('Unexpected server response.'); }
  }

  async refresh() {
    try {
      const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await this.safeJson(response);
      this.renderCart(cart);
      this.updateCartCount(cart.item_count);
      this.updateShippingBar(cart);
    } catch (err) {
      console.error('[CartDrawer] refresh failed:', err);
    }
  }

  renderCart(cart) {
    if (!this.itemsContainer) return;

    if (cart.item_count === 0) {
      this.itemsContainer.innerHTML = `
        <div class="cart-empty" style="text-align:center;padding:4rem 0;">
          <p style="font-size:1.6rem;margin-bottom:2rem;">Your cart is empty.</p>
          <a href="/collections/all" class="btn btn--primary">Shop Now</a>
        </div>`;
    } else {
      this.itemsContainer.innerHTML = cart.items.map(item => this.renderCartItem(item)).join('');
    }

    // Update footer subtotal (these elements are outside itemsContainer)
    const footerEl = this.drawer?.querySelector('.cart-drawer__footer');
    const subtotalEl = this.drawer?.querySelector('.cart-drawer__subtotal-amount');
    const subtotalLabelEl = this.drawer?.querySelector('.cart-drawer__subtotal-label');
    if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);
    if (subtotalLabelEl) {
      subtotalLabelEl.textContent = cart.item_count === 1
        ? 'Subtotal (1 item)'
        : `Subtotal (${cart.item_count} items)`;
    }
    // Show/hide footer based on cart contents
    if (footerEl) footerEl.style.display = cart.item_count > 0 ? '' : 'none';
  }

  renderCartItem(item) {
    const hasSellingPlan = item.selling_plan_allocation != null;
    // Show subscribe upsell for all one-time items
    // (We always show it; if the product has no selling plans the click will show an error)
    const showSubscribeUpsell = !hasSellingPlan;

    // Use Shopify image size suffix for thumbnail
    const imgSrc = item.image
      ? item.image.replace(/(\.[^.?]+)(\?.*)?$/, '_72x72_crop_center$1$2')
      : '';

    return `
      <div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">
          <a href="${item.url}">
            ${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHtml(item.title)}" width="72" height="72" loading="lazy">` : ''}
          </a>
        </div>
        <div class="cart-item__body">
          <p class="cart-item__title">${this.escapeHtml(item.product_title)}</p>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
          ${hasSellingPlan
            ? `<p class="cart-item__plan-name">${this.escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
          <div class="cart-item__qty">
            <button class="cart-item__qty-btn" data-key="${item.key}" data-delta="down" aria-label="Decrease quantity">−</button>
            <span class="cart-item__qty-count">${item.quantity}</span>
            <button class="cart-item__qty-btn" data-key="${item.key}" data-delta="up" aria-label="Increase quantity">+</button>
          </div>
          ${showSubscribeUpsell ? `
            <div class="cart-item__subscribe-upsell">
              <button class="cart-item__subscribe-btn"
                data-key="${item.key}"
                data-handle="${this.escapeHtml(item.handle)}">
                Subscribe &amp; save 20% + free shipping
              </button>
            </div>` : ''}
        </div>
        <div class="cart-item__right">
          <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove ${this.escapeHtml(item.title)}">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
              <path d="M10 11v6"></path><path d="M14 11v6"></path>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
            </svg>
          </button>
          <span class="cart-item__price">${this.formatMoney(item.final_line_price)}</span>
        </div>
      </div>`;
  }

  async updateQuantity(key, delta) {
    try {
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await this.safeJson(cartRes);
      const item = cart.items.find(i => i.key === key);
      if (!item) return;
      const newQty = delta === 'up' ? item.quantity + 1 : Math.max(0, item.quantity - 1);
      const updateRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      });
      const updatedCart = await this.safeJson(updateRes);
      this.renderCart(updatedCart);
      this.updateCartCount(updatedCart.item_count);
      this.updateShippingBar(updatedCart);
    } catch (err) {
      console.error('[CartDrawer] updateQuantity failed:', err);
    }
  }

  async removeItem(key) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      const cart = await this.safeJson(res);
      this.renderCart(cart);
      this.updateCartCount(cart.item_count);
      this.updateShippingBar(cart);
    } catch (err) {
      console.error('[CartDrawer] removeItem failed:', err);
    }
  }

  /**
   * Upgrade a one-time cart item to a subscription.
   * Shopify's /cart/change.js cannot add a selling_plan to an existing line item,
   * so we remove the item and re-add it with the selling plan.
   */
  async upgradeToSubscription(key, handle) {
    const btn = this.drawer?.querySelector(`.cart-item__subscribe-btn[data-key="${key}"]`);
    const originalText = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Updating...'; }

    try {
      // 1. Get current cart item details
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await this.safeJson(cartRes);
      const item = cart.items.find(i => i.key === key);
      if (!item) throw new Error('Item not found in cart');

      // 2. Fetch product selling plans
      const productRes = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
      const product = await this.safeJson(productRes);
      const groups = product.selling_plan_groups;
      if (!groups || groups.length === 0) throw new Error('No subscription plans available');
      const sellingPlanId = groups[0].selling_plans[0]?.id;
      if (!sellingPlanId) throw new Error('No selling plan ID found');

      // 3. Remove current one-time item
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      // 4. Re-add with selling plan
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{ id: item.variant_id, quantity: item.quantity, selling_plan: sellingPlanId }]
        })
      });
      const addData = await this.safeJson(addRes);
      if (addData.status && addData.status >= 400) throw new Error(addData.description || 'Could not upgrade');

      // 5. Refresh cart display
      await this.refresh();
    } catch (err) {
      console.error('[CartDrawer] upgradeToSubscription failed:', err);
      if (btn) { btn.disabled = false; btn.innerHTML = originalText; }
    }
  }

  updateCartCount(count) {
    this.cartCountEls.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'inline-flex' : 'none';
    });
  }

  updateShippingBar(cart) {
    const shippingBar = this.drawer?.querySelector('.cart-drawer__shipping-bar');
    if (!this.shippingFill) return;

    // Always show the bar
    if (shippingBar) shippingBar.style.display = '';

    // If any cart item has a subscription, treat as goal achieved — subscriptions always ship free
    const hasSubscription = cart && cart.items && cart.items.some(function(item) {
      return item.selling_plan_allocation != null;
    });

    if (hasSubscription) {
      this.shippingFill.style.width = '100%';
      if (this.shippingUnlock) {
        this.shippingUnlock.innerHTML = '<strong>Free shipping included</strong> with your subscription! 🎉';
      }
      return;
    }

    const totalPrice = cart ? cart.total_price : 0;
    const pct = Math.min(100, (totalPrice / this.threshold) * 100);
    this.shippingFill.style.width = `${pct}%`;
    if (this.shippingUnlock) {
      if (totalPrice >= this.threshold) {
        this.shippingUnlock.innerHTML = '<strong>You\'ve unlocked free shipping!</strong> 🎉';
      } else {
        const remaining = this.formatMoney(this.threshold - totalPrice);
        this.shippingUnlock.innerHTML = `Add ${remaining} more for <strong>free shipping!</strong>`;
      }
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});

document.addEventListener('cart:add', () => {
  if (window.cartDrawer) window.cartDrawer.open();
});
